import { useState } from 'react'

import type { Media } from '@/api/types'
import { useT } from '@/i18n'
import { cn } from '@/lib/cn'

import { VideoPlayer } from './VideoPlayer'

interface Props {
  media: Media[]
  /** 详情页用大图，时间线用 medium */
  size?: 'feed' | 'detail'
}

/** 1–4 张图的网格，用宽高比占位避免加载时跳动。 */
export function MediaGrid({ media, size = 'feed' }: Props) {
  const t = useT()
  const [lightbox, setLightbox] = useState<Media | null>(null)
  if (media.length === 0) return null

  const single = media.length === 1
  const aspect = single ? computeAspect(media[0]) : undefined

  // 视频总是单独一个（D92）
  if (media[0].kind === 'video') {
    return (
      <div className="mt-3 overflow-hidden rounded-md border border-line">
        <VideoPlayer media={media[0]} aspect={computeAspect(media[0], true)} />
      </div>
    )
  }

  return (
    <>
      <div
        className={cn(
          'mt-3 grid overflow-hidden rounded-md border border-line',
          single && 'grid-cols-1',
          media.length === 2 && 'aspect-[16/9] grid-cols-2 gap-0.5',
          media.length === 3 && 'aspect-[16/9] grid-cols-2 grid-rows-2 gap-0.5',
          media.length >= 4 && 'aspect-[16/9] grid-cols-2 grid-rows-2 gap-0.5',
        )}
        style={single && aspect ? { aspectRatio: aspect } : undefined}
      >
        {media.slice(0, 4).map((item, index) => (
          <button
            key={item.id}
            type="button"
            onClick={(event) => {
              event.stopPropagation()
              setLightbox(item)
            }}
            className={cn(
              'relative size-full overflow-hidden bg-surface-subtle',
              media.length === 3 && index === 0 && 'row-span-2',
            )}
            aria-label={item.alt_text || t('media.view')}
          >
            <img
              src={(size === 'detail' ? item.medium_url : item.thumb_url) ?? item.url ?? undefined}
              alt={item.alt_text ?? ''}
              loading="lazy"
              className="size-full object-cover"
            />
            {item.alt_text && (
              <span className="absolute bottom-1 left-1 rounded bg-black/70 px-1 text-[11px] font-bold text-white">
                ALT
              </span>
            )}
          </button>
        ))}
      </div>

      {lightbox && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/90 p-4"
          onClick={(event) => {
            event.stopPropagation()
            setLightbox(null)
          }}
          role="dialog"
          aria-modal="true"
        >
          <img
            src={lightbox.url ?? undefined}
            alt={lightbox.alt_text ?? ''}
            className="max-h-full max-w-full object-contain"
          />
        </div>
      )}
    </>
  )
}

function computeAspect(item: Media, video = false): string | undefined {
  if (!item.width || !item.height) return '16 / 9'
  // 极端长图裁到 4:5，避免一张图占满整屏；竖屏视频放宽到 9:16 的一半高度以内（3:4）
  const ratio = item.width / item.height
  const clamped = Math.min(Math.max(ratio, video ? 0.75 : 0.8), 2)
  return `${clamped} / 1`
}
